import { dispatch } from '@/redux/index';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAutoregistryMonthDots,
  getPatientAutoregistry,
  sendPatientAutoregistry,
  getAutoregistryTemplates,
  createAutoregistryTemplate,
  updateAutoregistryTemplate,
  deleteAutoregistryTemplate,
  getScheduledAutoregisters,
  createScheduledAutoregister,
  updateScheduledAutoregister,
  deleteScheduledAutoregister,
} from '../../services/patient/patient.service';

export const fetchAutoregistry = createAsyncThunk(
  'autoregistry/fetchAutoregistry',
  async ({
    patientId,
    from,
    to,
  }: {
    patientId: string;
    from: string;
    to: string;
  }) => {
    const response = await getPatientAutoregistry(patientId, from, to);
    return response;
  }
);

export const sendAutoregistry = createAsyncThunk(
  'autoregistry/sendAutoregistry',
  async (
    {
      therapist_id,
      patient_id,
      name,
      questions,
      schedule,
    }: {
      therapist_id: string;
      patient_id: string;
      name: string;
      questions: any[];
      schedule?: {
        schedule_type: 'daily' | 'weekly' | 'monthly';
        schedule_time: string;
        schedule_day?: number;
        timezone: string;
      } | null;
    },
    thunkAPI
  ) => {
    try {
      if (schedule) {
        await createScheduledAutoregister(
          name,
          questions,
          patient_id,
          therapist_id,
          schedule.schedule_type,
          schedule.schedule_time,
          schedule.schedule_day,
          schedule.timezone
        );
        return { success: true, scheduled: true };
      } else {
        const data: any = await sendPatientAutoregistry(
          patient_id,
          therapist_id,
          name,
          questions
        );
        return data;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue('Error sending autoregistry');
    }
  }
);

export const getAutoregistryMonthMap = createAsyncThunk(
  'autoregistry/getAutoregistryMonthMap',
  async ({
    patientId,
    from,
    to,
  }: {
    patientId: string;
    from: string;
    to: string;
  }) => {
    const response = await getAutoregistryMonthDots(patientId, from, to);

    return response;
  }
);

export const fetchAutoregistryTemplates = createAsyncThunk(
  'autoregistry/fetchTemplates',
  async () => {
    const response = await getAutoregistryTemplates();
    return response.data;
  }
);

export const createTemplate = createAsyncThunk(
  'autoregistry/createTemplate',
  async ({
    name,
    questions,
    default_time,
  }: {
    name: string;
    questions: string[];
    default_time?: string;
  }) => {
    const response = await createAutoregistryTemplate(
      name,
      questions,
      default_time
    );
    return response.data;
  }
);

export const updateTemplate = createAsyncThunk(
  'autoregistry/updateTemplate',
  async ({
    id,
    name,
    questions,
    default_time,
  }: {
    id: string;
    name: string;
    questions: string[];
    default_time?: string;
  }) => {
    const response = await updateAutoregistryTemplate(
      id,
      name,
      questions,
      default_time
    );
    return response.data;
  }
);

export const deleteTemplate = createAsyncThunk(
  'autoregistry/deleteTemplate',
  async (id: string) => {
    await deleteAutoregistryTemplate(id);
    return id;
  }
);

export const fetchScheduledAutoregisters = createAsyncThunk(
  'autoregistry/fetchScheduledAutoregisters',
  async (patientId: string) => {
    const response = await getScheduledAutoregisters(patientId);
    return response;
  }
);

export const createSchedule = createAsyncThunk(
  'autoregistry/createSchedule',
  async ({
    name,
    questions,
    patient_id,
    therapist_id,
    schedule_type,
    schedule_time,
    schedule_day,
    timezone,
  }: {
    name: string;
    questions: any[];
    patient_id: string;
    therapist_id: string;
    schedule_type: 'daily' | 'weekly' | 'monthly';
    schedule_time: string;
    schedule_day?: number;
    timezone: string;
  }) => {
    const response = await createScheduledAutoregister(
      name,
      questions,
      patient_id,
      therapist_id,
      schedule_type,
      schedule_time,
      schedule_day,
      timezone
    );
    return response;
  }
);

export const updateSchedule = createAsyncThunk(
  'autoregistry/updateSchedule',
  async ({
    id,
    name,
    questions,
    schedule_type,
    schedule_time,
    schedule_day,
    timezone,
    active,
  }: {
    id: string;
    name?: string;
    questions?: any[];
    schedule_type?: 'daily' | 'weekly' | 'monthly';
    schedule_time?: string;
    schedule_day?: number;
    timezone?: string;
    active?: boolean;
  }) => {
    const response = await updateScheduledAutoregister(
      id,
      name,
      questions,
      schedule_type,
      schedule_time,
      schedule_day,
      timezone,
      active
    );

    return response;
  }
);

export const deleteSchedule = createAsyncThunk(
  'autoregistry/deleteSchedule',
  async (id: string) => {
    await deleteScheduledAutoregister(id);
    return id;
  }
);
